import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertColor,
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import axiosInstance from '../../../../utils/axios';
import { URLS } from '../../../../utils/constants/urls';
import Notification from '../../../../components/Notification';
import { useNavigate, useParams } from 'react-router-dom';
import PrimaryButton from '../../../../components/Button/PrimaryButton';
import './role.scss';
import { useDispatch } from 'react-redux';
import { routes } from '../../../../utils/constants/routes';
import strings from '../../../../common/Translation/Translate';
import { refreshToken } from '../../../../utils/redux/reducer/authentication-slice';
import { pageSubtitleSx, pageTitleSx } from 'utils/styles/pageHeader';

interface PrivilegeItem {
  id: number;
  name: string;
  description?: string;
  selected: boolean;
}

interface PrivilegeGroup {
  groupName: string;
  privilegesInGroup: PrivilegeItem[];
}

const Privilege: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeGroup, setActiveGroup] = useState<string>('');
  const [privilegeGroups, setPrivilegeGroups] = useState<PrivilegeGroup[]>([]);
  const [roleName, setRoleName] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT. */
  const savingRef = useRef(false);
  const [notification, setNotification] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const totalPrivileges = useMemo(
    () => privilegeGroups.reduce((sum, group) => sum + group.privilegesInGroup.length, 0),
    [privilegeGroups],
  );

  const activeGroupData = useMemo(
    () => privilegeGroups.find(group => group.groupName === activeGroup) || null,
    [privilegeGroups, activeGroup],
  );

  const activeSelectedCount = activeGroupData
    ? activeGroupData.privilegesInGroup.filter(p => p.selected).length
    : 0;
  const activeTotalCount = activeGroupData?.privilegesInGroup.length || 0;

  const fetchPrivileges = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.get(URLS.editRole(id));
      setRoleName(data.name);

      const selected: number[] = [];
      (data.privileges || []).forEach((group: PrivilegeGroup) => {
        group.privilegesInGroup.forEach((p: PrivilegeItem) => {
          if (p.selected) selected.push(p.id);
        });
      });
      setPrivilegeGroups(data.privileges || []);
      setSelectedIds(selected);
      const firstGroup = data.privileges?.[0]?.groupName || '';
      setActiveGroup((prev: string) => prev || firstGroup);
    } catch (e: any) {
      setNotification({
        display: true,
        severity: 'error',
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPrivileges();
  }, [fetchPrivileges]);

  const updateSelections = (
    checked: boolean,
    privilegeId: number | null,
    filterFn?: (privilege: PrivilegeItem) => boolean,
  ) => {
    const updated = privilegeGroups.map(group => ({
      ...group,
      privilegesInGroup: group.privilegesInGroup.map(p => {
        const shouldUpdate = privilegeId !== null ? p.id === privilegeId : filterFn ? filterFn(p) : true;
        return { ...p, selected: shouldUpdate ? checked : p.selected };
      }),
    }));

    if (
      checked &&
      filterFn &&
      (filterFn({ name: 'EDIT_' } as PrivilegeItem) || filterFn({ name: 'DELETE_' } as PrivilegeItem))
    ) {
      updated.forEach(group => {
        group.privilegesInGroup.forEach(p => {
          if (p.name.startsWith('VIEW_')) p.selected = true;
        });
      });
    }

    const flat = updated.flatMap(g => g.privilegesInGroup);
    setPrivilegeGroups(updated);
    setSelectedIds(flat.filter(p => p.selected).map(p => p.id));
  };

  const handleSave = async () => {
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    setIsLoading(true);
    try {
      const payload = { name: roleName, privilegeIds: selectedIds };
      const { status } = await axiosInstance.put(URLS.updatePriviliges(id!), payload);
      if (status === 200) {
        setNotification({ display: true, severity: 'success', message: strings.privilege_success_message });
        setTimeout(() => navigate(routes.roleList), 1000);
        await dispatch(refreshToken() as any);
      }
    } catch (e: any) {
      setNotification({
        display: true,
        severity: 'error',
        message: e.response?.data?.message || 'Please try again',
      });
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const getPastelColor = (name: string) => {
    if (name.startsWith('VIEW_')) return '#cce5ff';
    if (name.startsWith('EDIT_')) return '#ffe0b2';
    if (name.startsWith('ADD_') || name.startsWith('CREATE_')) return '#d0f0c0';
    if (name.startsWith('DELETE_')) return '#f8d7da';
    return '#f0f0f0';
  };

  const groupSelectedCount = (group: PrivilegeGroup) => group.privilegesInGroup.filter(p => p.selected).length;

  return (
    <Container maxWidth={false} sx={{ p: 0 }} className="privileges">
      {isLoading && (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      )}

      {notification.display && (
        <Notification
          isOpen={notification.display}
          message={notification.message}
          severity={notification.severity as AlertColor}
          closeNotification={() => setNotification({ ...notification, display: false })}
          autoHideDuration={2000}
        />
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}>
        <Box>
          <Typography sx={pageTitleSx}>
            {roleName || strings.customermgmt_column_role}
          </Typography>
          <Typography sx={{ ...pageSubtitleSx, mt: 0.5 }}>
            Selected {selectedIds.length}/{totalPrivileges}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <PrimaryButton variant="outlined" fullWidth={false} onClick={() => navigate(routes.roleList)}>
            {strings.cancelText || 'Cancel'}
          </PrimaryButton>
          <PrimaryButton
            variant="contained"
            fullWidth={false}
            onClick={handleSave}
            disabled={isLoading}
            data-testid="saveButton">
            {strings.common_button_save}
          </PrimaryButton>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
          minHeight: 520,
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            borderRight: { md: theme => `1px solid ${theme.palette.divider}` },
            borderBottom: { xs: theme => `1px solid ${theme.palette.divider}`, md: 'none' },
            bgcolor: 'grey.50',
            overflow: 'auto',
          }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
              Groups
            </Typography>
          </Box>
          <Divider />
          <List dense disablePadding>
            {privilegeGroups.map(group => {
              const selectedCount = groupSelectedCount(group);
              const total = group.privilegesInGroup.length;
              const allSelected = total > 0 && selectedCount === total;
              const someSelected = selectedCount > 0 && !allSelected;
              return (
                <ListItemButton
                  key={group.groupName}
                  selected={activeGroup === group.groupName}
                  onClick={() => setActiveGroup(group.groupName)}
                  sx={{
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    gap: 0.5,
                    '&.Mui-selected': { bgcolor: 'primary.50' },
                  }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={e => e.stopPropagation()}
                      onChange={e =>
                        updateSelections(e.target.checked, null, p =>
                          group.privilegesInGroup.some(gp => gp.id === p.id),
                        )
                      }
                    />
                  </ListItemIcon>
                  <Typography
                    fontWeight={600}
                    fontSize="0.875rem"
                    sx={{ flex: 1, minWidth: 0, pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {group.groupName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
                    {selectedCount}/{total}
                  </Typography>
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        <Box sx={{ p: 2.5, overflow: 'auto' }}>
          {activeGroupData ? (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} fontSize="1rem">
                    {activeGroupData.groupName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeSelectedCount}/{activeTotalCount} selected
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={activeTotalCount > 0 && activeSelectedCount === activeTotalCount}
                      indeterminate={activeSelectedCount > 0 && activeSelectedCount < activeTotalCount}
                      onChange={e =>
                        updateSelections(e.target.checked, null, p =>
                          activeGroupData.privilegesInGroup.some(gp => gp.id === p.id),
                        )
                      }
                    />
                  }
                  label="Select all in group"
                />
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={1.5}>
                {activeGroupData.privilegesInGroup.map(p => (
                  <Grid item xs={12} sm={6} md={4} key={p.id}>
                    <FormControlLabel
                      sx={{
                        m: 0,
                        width: '100%',
                        alignItems: 'center',
                        px: 1,
                        py: 0.75,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={p.selected}
                          onChange={e => updateSelections(e.target.checked, p.id)}
                        />
                      }
                      label={
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: getPastelColor(p.name),
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                          }}>
                          {p.name}
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Typography color="text.secondary">Select a privilege group</Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Privilege;
